/**
 * Conversão e normalização de unidades (massa / volume / contagem).
 * Espelha functions/src/utils/unidadeMedida.ts
 */

const FATOR_PARA_KG = {
  g: 0.001,
  grama: 0.001,
  gramas: 0.001,
  kg: 1,
  kilo: 1,
  kilos: 1,
  kilogramas: 1,
  quilograma: 1,
  quilogramas: 1,
  ton: 1000,
  t: 1000,
  tonelada: 1000,
  toneladas: 1000
};

const FATOR_PARA_L = {
  ml: 0.001,
  mililitro: 0.001,
  mililitros: 0.001,
  l: 1,
  litro: 1,
  litros: 1
};

const CONTAGEM = new Set(['unid', 'unid.', 'un', 'und', 'unidade', 'unidades', 'pc', 'pç', 'peca', 'peça']);

export function chaveUnidade(unidade) {
  return String(unidade || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
}

export function rotuloUnidade(unidade) {
  const k = chaveUnidade(unidade);
  if (!k) return 'Kg';
  if (k === 'g' || k === 'grama' || k === 'gramas') return 'g';
  if (k === 'ton' || k === 't' || k === 'tonelada' || k === 'toneladas') return 'Ton';
  if (k === 'ml' || k === 'mililitro' || k === 'mililitros') return 'mL';
  if (k === 'l' || k === 'litro' || k === 'litros') return 'L';
  if (CONTAGEM.has(k) || k === '%') return k === '%' ? '%' : 'Unid.';
  if (k === 'kg' || k === 'kilo' || k === 'kilos' || k === 'quilograma' || k === 'quilogramas') return 'Kg';
  return String(unidade || 'Kg').trim() || 'Kg';
}

export function dimensaoUnidade(unidade) {
  const k = chaveUnidade(unidade);
  if (!k) return 'desconhecida';
  if (k === '%') return 'percentual';
  if (CONTAGEM.has(k)) return 'contagem';
  if (FATOR_PARA_KG[k] != null) return 'massa';
  if (FATOR_PARA_L[k] != null) return 'volume';
  return 'desconhecida';
}

export function unidadesCompativeis(de, para) {
  const a = chaveUnidade(de);
  const b = chaveUnidade(para);
  if (!a || !b) return true;
  if (a === b) return true;
  const da = dimensaoUnidade(a);
  const db = dimensaoUnidade(b);
  if (da === 'desconhecida' || db === 'desconhecida') {
    return rotuloUnidade(a).toLowerCase() === rotuloUnidade(b).toLowerCase();
  }
  return da === db && da !== 'percentual';
}

export function convertQuantidade(valor, de, para) {
  if (!Number.isFinite(valor)) {
    throw new Error('Quantidade inválida para conversão');
  }
  const a = chaveUnidade(de) || 'kg';
  const b = chaveUnidade(para) || a;
  if (a === b || !para || !de) return valor;

  if (!unidadesCompativeis(a, b)) {
    throw new Error(`Unidades incompatíveis: "${rotuloUnidade(de)}" → "${rotuloUnidade(para)}"`);
  }

  const dim = dimensaoUnidade(a);
  if (dim === 'contagem' || dim === 'percentual') return valor;

  if (dim === 'massa') {
    const fa = FATOR_PARA_KG[a];
    const fb = FATOR_PARA_KG[b];
    if (fa == null || fb == null) throw new Error(`Não foi possível converter massa: ${de} → ${para}`);
    return (valor * fa) / fb;
  }

  if (dim === 'volume') {
    const fa = FATOR_PARA_L[a];
    const fb = FATOR_PARA_L[b];
    if (fa == null || fb == null) throw new Error(`Não foi possível converter volume: ${de} → ${para}`);
    return (valor * fa) / fb;
  }

  return valor;
}

export function tryConvertQuantidade(valor, de, para) {
  try {
    return convertQuantidade(valor, de, para);
  } catch {
    return null;
  }
}
